import React, { useEffect, useRef, useState } from "react";
import { User, Building2, Lock, Save } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Input from "../../components/Input/Input";
import Modal from "../../components/Modal/Modal";
import Notification from "../../components/Notification/Notification";
import Button from "../../components/Button/Button";
import ToggleButton from "./ToggleButton";

/* ===================== SERVICES ===================== */
import { apiGet, apiPut } from "../../apiService";

/* ===================== STYLES ===================== */
import styles from "./UserProfile.module.scss";

const UserProfile = () => {
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const [profileData, setProfileData] = useState(null);
  const [displayName, setDisplayName] = useState("");

  const [loading, setLoading] = useState(true);
  const [industryOptions, setIndustryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [updating, setUpdating] = useState(false);

  const canEditOrgFields =
    profileData?.roleType === "Internal" ||
    (profileData?.roleType === "Client" && profileData?.roleName === "Admin");

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const [profileErrors, setProfileErrors] = useState({});

  // State for password change
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState({});
  const hasFetched = useRef(false);

  // Fetch data
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchIndustryData = async () => {
      try {
        const response = await apiGet("user", "/master/industry");
        if (response && response.data) {
          setIndustryOptions(response.data);
        }
      } catch (error) {
        console.error("Error fetching industry data:", error);
      }
    };

    const fetchStateData = async () => {
      try {
        const response = await apiGet("user", "/master/state");
        if (response && response.data) {
          setStateOptions(response.data);
        }
      } catch (error) {
        console.error("Error fetching state data:", error);
      }
    };

    const fetchProfileData = async () => {
      try {
        const response = await apiGet("user", "/profile");
        if (response.status === "success") {
          const profile = response.data;

          if (profile?.companyName) {
            localStorage.setItem("companyName", profile.companyName);
          }

          setProfileData({
            ...profile,
            industry: profile.industry
              ? { id: profile.industry.id, label: profile.industry.name }
              : null,
            state: profile.state || null,
          });
          setDisplayName(profile.name);
        }
      } catch (error) {
        console.error("Error fetching profile data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchIndustryData();
    fetchProfileData();
    fetchStateData();
  }, []);

  const handleProfileUpdate = async () => {
    let errors = {};

    // BASIC VALIDATION
    if (!profileData.name?.trim()) {
      errors.name = "This field is required";
    }

    if (!profileData.gstNumber?.trim()) {
      errors.gstNumber = "This field is required";
    } else if (
      !/^(?=.*[A-Z])(?=.*[0-9])[A-Z0-9]{15}$/.test(profileData.gstNumber.trim())
    ) {
      errors.gstNumber = "GST must be exactly 15 chars with letters & numbers";
    }

    if (!profileData.email?.trim()) {
      errors.email = "This field is required";
    } else if (!emailRegex.test(profileData.email.trim())) {
      errors.email = "Enter a valid email address";
    }

    if (!profileData.mobile?.trim()) {
      errors.mobile = "This field is required";
    }

    if (!profileData.companyName?.trim()) {
      errors.companyName = "This field is required";
    }

    if (!profileData.designation?.trim()) {
      errors.designation = "This field is required";
    }

    if (!profileData.industry?.id) {
      errors.industry = "This field is required";
    }

    if (canEditOrgFields) {
      if (!profileData.address?.trim()) {
        errors.address = "This field is required";
      }
      if (!profileData.state?.name) {
        errors.state = "This field is required";
      }
    }

    if (Object.keys(errors).length > 0) {
      setProfileErrors(errors);
      return;
    }

    setProfileErrors({});

    const updatedProfileData = {
      name: profileData.name,
      email: profileData.email,
      companyName: profileData.companyName,
      designation: profileData.designation,
      industry: profileData.industry?.id,
      mobile: profileData.mobile,
      ...(canEditOrgFields && {
        state: profileData.state?.name || null,
        address: profileData.address || null,
        gstNumber: profileData.gstNumber || null,
      }),
    };

    try {
      setUpdating(true);
      const response = await apiPut(
        "user",
        "/profile/update",
        updatedProfileData,
      );

      if (response.status === "success") {
        setDisplayName(profileData.name);
        const updatedCompanyName =
          response.data?.companyName || profileData.companyName;
        if (updatedCompanyName) {
          localStorage.setItem("companyName", updatedCompanyName);
        }
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
    } finally {
      setUpdating(false);
    }
  };

  const handlePasswordUpdate = async () => {
    setPasswordErrors({});
    let errors = {};

    if (!currentPassword) {
      errors.currentPassword = "This field is required";
    }

    if (!newPassword) {
      errors.newPassword = "This field is required";
    } else {
      const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;
      if (!passwordRegex.test(newPassword)) {
        errors.newPassword =
          "Password must be at least 8 characters long and include 1 number and 1 special character";
      }
    }

    if (!confirmPassword) {
      errors.confirmPassword = "This field is required";
    } else if (newPassword && newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    const passwordData = {
      currentPassword,
      newPassword,
      confirmPassword,
    };

    try {
      setUpdating(true);
      const response = await apiPut(
        "user",
        "/profile/change-password",
        passwordData,
      );

      if (response.status === "success") {
        setShowPasswordModal(false);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      } else {
        setPasswordErrors({
          apiError: response.message || "Error changing password",
        });
      }
    } catch (error) {
      console.error("Error changing password:", error);
      setPasswordErrors({
        apiError: "An error occurred. Please try again later.",
      });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>; // Could replace with a skeleton component later
  }

  if (!profileData) {
    return <div>Error loading profile data.</div>;
  }

  return (
    <div className={styles["admin-user-profile"]}>
      {/* SUCCESS TOAST */}
      {showSuccess && (
        <div className={styles["admin-user-profile__toast-container"]}>
          <Notification message="Profile Updated Successfully" type="success" />
        </div>
      )}

      {/* HEADER SECTION */}
      <div className={styles["admin-user-profile__card"]}>
        <div className={styles["admin-user-profile__header-content"]}>
          <div className={styles["admin-user-profile__user-info"]}>
            <div className={styles["admin-user-profile__avatar"]}>
              {profileData.initials}
            </div>
            <div>
              <h4 className={styles["admin-user-profile__name"]}>
                {displayName}
              </h4>
              <p className={styles["admin-user-profile__role"]}>
                {profileData.roleName}
              </p>
            </div>
          </div>

          <div className={styles["admin-user-profile__actions"]}>
            <ToggleButton />
            <button
              className={`${styles["admin-user-profile__btn"]} ${styles["admin-user-profile__btn--secondary"]}`}
              onClick={() => setShowPasswordModal(true)}
            >
              <Lock size={16} strokeWidth={2.5} />
              Update Password
            </button>
          </div>
        </div>
      </div>

      {/* ACCOUNT INFO SECTION */}
      <div className={styles["admin-user-profile__card"]}>
        <h5 className={styles["admin-user-profile__section-title"]}>
          <div className={styles["admin-user-profile__icon-wrapper"]}>
            <User size={18} strokeWidth={2.5} />
          </div>
          Account Information
        </h5>

        <div className={styles["admin-user-profile__grid"]}>
          <Input
            label="Full Name"
            value={profileData.name}
            onChange={(e) =>
              setProfileData({ ...profileData, name: e.target.value })
            }
            error={profileErrors.name}
            placeholder="Enter your full name"
            required
          />

          <Input label="Email ID" value={profileData.email} disabled />

          <Input
            label="Mobile No"
            value={profileData.mobile || ""}
            onChange={(e) => {
              let value = e.target.value.replace(/\D/g, "");
              if (value.length > 10) value = value.slice(0, 10);
              setProfileData({ ...profileData, mobile: value });
            }}
            error={profileErrors.mobile}
            placeholder="Enter 10 digit mobile number"
            required
          />

          <Input
            label="Designation"
            value={profileData.designation}
            onChange={(e) =>
              setProfileData({ ...profileData, designation: e.target.value })
            }
            error={profileErrors.designation}
            required
            placeholder="Enter your designation"
          />

          <Input
            label="Industry"
            type="select"
            options={industryOptions.map((industry) => ({
              label: industry.label,
              value: industry.id,
            }))}
            value={profileData.industry?.id || ""}
            onChange={(e) =>
              setProfileData((prevData) => ({
                ...prevData,
                industry: { ...prevData.industry, id: e.target.value },
              }))
            }
            error={profileErrors.industry}
            required
          />
        </div>
      </div>

      {/* ORGANIZATION INFO SECTION */}
      <div className={styles["admin-user-profile__card"]}>
        <h5 className={styles["admin-user-profile__section-title"]}>
          <div className={styles["admin-user-profile__icon-wrapper"]}>
            <Building2 size={18} strokeWidth={2.5} />
          </div>
          Organization Information
        </h5>

        <div className={styles["admin-user-profile__grid"]}>
          <Input
            label="Organization Name"
            value={profileData.companyName}
            onChange={(e) =>
              setProfileData({ ...profileData, companyName: e.target.value })
            }
            error={canEditOrgFields ? profileErrors.companyName : ""}
            required={canEditOrgFields}
            disabled={!canEditOrgFields}
            placeholder="Enter your organization name"
          />

          <Input
            label="Address"
            value={profileData.address || ""}
            onChange={(e) =>
              setProfileData({ ...profileData, address: e.target.value })
            }
            error={canEditOrgFields ? profileErrors.address : ""}
            required={canEditOrgFields}
            disabled={!canEditOrgFields}
            placeholder="Enter your organization address"
          />

          <Input
            label="State"
            type="select"
            options={stateOptions.map((state) => ({
              label: state.label,
              value: state.label,
            }))}
            value={profileData.state?.value || ""}
            onChange={(e) =>
              setProfileData((prev) => ({
                ...prev,
                state: { name: e.target.value, value: e.target.value },
              }))
            }
            error={canEditOrgFields ? profileErrors.state : ""}
            required={canEditOrgFields}
            disabled={!canEditOrgFields}
          />

          <Input
            label="GST Number"
            value={profileData.gstNumber || ""}
            onChange={(e) => {
              let value = e.target.value.toUpperCase();
              if (value.length > 15) value = value.slice(0, 15);
              setProfileData({ ...profileData, gstNumber: value });
            }}
            error={canEditOrgFields ? profileErrors.gstNumber : ""}
            required={canEditOrgFields}
            disabled={!canEditOrgFields}
            placeholder="22AAAAA0000A1Z5"
          />
        </div>
      </div>

      {/* FOOTER ACTIONS */}
      <div className={styles["admin-user-profile__footer"]}>
        <button
          className={`${styles["admin-user-profile__btn"]} ${styles["admin-user-profile__btn--primary"]}`}
          onClick={handleProfileUpdate}
          disabled={updating}
        >
          <Save size={18} strokeWidth={2.5} />
          {updating ? "Updating..." : "Update Profile"}
        </button>
      </div>

      {/* CHANGE PASSWORD MODAL */}
      {showPasswordModal && (
        <Modal
          title="Change Password"
          onClose={() => setShowPasswordModal(false)}
        >
          <div className={styles["admin-user-profile__password-form"]}>
            <Input
              label="Current Password"
              type="password"
              placeholder="Enter current password"
              showPasswordToggle={true}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              error={passwordErrors.currentPassword}
              required
            />
            <Input
              label="New Password"
              type="password"
              placeholder="Enter new password"
              showPasswordToggle={true}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              error={passwordErrors.newPassword}
              required
            />
            <Input
              label="Confirm New Password"
              type="password"
              placeholder="Confirm new password"
              showPasswordToggle={true}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={passwordErrors.confirmPassword}
              required
            />

            {passwordErrors.apiError && (
              <Notification message={passwordErrors.apiError} type="error" />
            )}

            <button
              className={`${styles["admin-user-profile__btn"]} ${styles["admin-user-profile__btn--primary"]} ${styles["admin-user-profile__btn--full"]}`}
              onClick={handlePasswordUpdate}
              disabled={updating}
            >
              {updating ? "Processing..." : "Continue"}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default UserProfile;
